/**
 * sessionTracker.ts — Dev telemetry for TheBurntPeanut Simulator
 *
 * Captures a running event log + periodic snapshots during a playthrough.
 * Bug reports automatically include the latest snapshot.
 * Runs auto-save to Supabase on game-over / win (dev users only).
 *
 * All data lives in module-level singletons — safe to import from anywhere.
 */

import type { GameState } from './types';
import { getWeaponSystemState } from './weaponAdapter';
import { getPercSlotDisplayData, getItemInventoryDisplayData } from './itemsAdapter';
import { enemyAdapterModule } from './enemyAdapter';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SessionEvent {
  ts: number;                        // seconds into run
  type: string;
  data: Record<string, unknown>;
}

export interface SessionSnapshot {
  capturedAt: number;                // seconds into run when snapshot was taken
  character: string;
  phase: string;
  floor: number;
  kills: number;
  timeSurvived: number;              // seconds
  // Player vitals
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  level: number;
  xp: number;
  xpToNext: number;
  gold: number;
  // Player stats
  armor: number;
  critChancePct: number;             // e.g. 25 = 25%
  critDamagePct: number;             // e.g. 150 = 150%
  damageBonusPct: number;            // e.g. 20 = +20%
  attackSpeedBonusPct: number;
  lifesteal: number;
  evasionPct: number;
  knockbackBonus: number;
  // Economy
  chestsOpened: number;
  // Loadout
  weapons: Array<{ id: string; level: number }>;
  perks: Array<{ name: string; level: number; bonus: string }>;
  items: Array<{ name: string; stacks: number }>;
  // World state
  enemiesAlive: number;
  enemyTypes: Record<string, number>;
}

export interface GameSessionRecord {
  username: string;
  character_id: string;
  character_name: string;
  floor_reached: number;
  kills: number;
  time_survived: number;
  outcome: 'win' | 'death' | 'quit';
  events: SessionEvent[];
  final_snapshot: SessionSnapshot | null;
}

// ─── Singleton state ──────────────────────────────────────────────────────────

// BETA 15.56 — DEV_USERS centralized in devUsers.ts. The const here was dead
// code (declared but never referenced). Import isDevUser from devUsers.ts if
// needed in future.

let _active = false;
let _username = '';
let _sessionStart = 0;
let _events: SessionEvent[] = [];
let _latestSnapshot: SessionSnapshot | null = null;

// ─── Public API ───────────────────────────────────────────────────────────────

/** Call once after initGame() succeeds — begins event capture. */
export function startSession(state: GameState, username: string): void {
  _active = true;
  _username = username;
  _sessionStart = Date.now();
  _events = [];
  _latestSnapshot = null;

  _log('session_start', {
    character: state.character?.id ?? 'unknown',
    characterName: state.character?.name ?? 'unknown',
    username,
  });
}

/**
 * Log a discrete game event (level-up choice, shrine activation, chest item, floor transition…)
 * Safe to call even when no session is active — silently ignored.
 */
export function logEvent(type: string, data: Record<string, unknown>): void {
  if (!_active) return;
  _log(type, data);
}

/**
 * Rebuild and cache a fresh snapshot from the current GameState.
 * Call this periodically from the game loop (e.g. every 5 s) so the
 * DevPanel always has reasonably fresh data.
 */
export function updateSnapshot(state: GameState): void {
  if (!_active) return;
  _latestSnapshot = _buildSnapshot(state);
}

/**
 * Returns the latest cached snapshot + full event log.
 * DevPanel calls this when the user opens or submits a bug report.
 */
export function getSessionData(): {
  snapshot: SessionSnapshot | null;
  events: SessionEvent[];
  username: string;
  active: boolean;
} {
  return {
    snapshot: _latestSnapshot,
    events: [..._events],
    username: _username,
    active: _active,
  };
}

/**
 * Call when the run ends (gameover, won, or manual quit to menu).
 * Saves the full session record to Supabase for all users.
 */
export async function endSession(state: GameState): Promise<void> {
  if (!_active) return;
  _active = false;

  // Determine outcome: use phase for death/win, fallback to 'quit' if still playing
  const outcome = state.phase === 'won'
    ? 'run_won'
    : state.phase === 'gameover'
      ? 'run_death'
      : 'run_quit';

  _log(outcome, {
    floor: state.wave ?? 1,
    kills: state.killCount,
    timeSurvived: Math.round(state.time),
  });

  _latestSnapshot = _buildSnapshot(state);

  const record: GameSessionRecord = {
    username: _username,
    character_id: state.character?.id ?? 'unknown',
    character_name: state.character?.name ?? 'unknown',
    floor_reached: state.wave ?? 1,
    kills: state.killCount,
    time_survived: Math.round(state.time),
    outcome: state.phase === 'won' ? 'win' : state.phase === 'gameover' ? 'death' : 'quit',
    events: _events,
    final_snapshot: _latestSnapshot,
  };

  try {
    const { saveGameSession } = await import('../lib/supabase');
    await saveGameSession(record);
  } catch (e) {
    console.error('[tracker] Session save failed:', e);
  }
}

// ─── Private helpers ──────────────────────────────────────────────────────────

function _log(type: string, data: Record<string, unknown>): void {
  const ts = _sessionStart ? Math.round((Date.now() - _sessionStart) / 1000) : 0;
  _events.push({ ts, type, data });
  if (_events.length > 120) _events.shift(); // keep last 120 events
}

function _buildSnapshot(state: GameState): SessionSnapshot {
  const ws = getWeaponSystemState();
  const weapons = (ws?.weapons ?? []).map(w => ({
    id: w.definitionId,
    level: w.level ?? 1,
  }));

  const perks = getPercSlotDisplayData().map(p => ({
    name: p.percName,
    level: p.level,
    bonus: p.totalBonusDisplay,
  }));

  const items = getItemInventoryDisplayData().map(i => ({
    name: i.name,
    stacks: i.stacks,
  }));

  // Enemy census
  const allEnemies = enemyAdapterModule.getEnemies();
  const alive = allEnemies.filter(e => e.alive);
  const enemyTypes: Record<string, number> = {};
  alive.forEach(e => {
    enemyTypes[e.type] = (enemyTypes[e.type] ?? 0) + 1;
  });

  const elapsed = Math.round(state.time);
  const p = state.player;

  return {
    capturedAt: _sessionStart ? Math.round((Date.now() - _sessionStart) / 1000) : 0,
    character: state.character?.id ?? 'unknown',
    phase: state.phase,
    floor: state.wave ?? 1,
    kills: state.killCount,
    timeSurvived: elapsed,
    hp: Math.round(p.hp),
    maxHp: Math.round(p.maxHp),
    shield: Math.round(p.shield ?? 0),
    maxShield: Math.round(p.maxShield ?? 0),
    level: p.level,
    xp: Math.round(p.xp),
    xpToNext: Math.round(p.xpToNext),
    gold: Math.round(p.gold),
    armor: Math.round(p.armor ?? 0),
    critChancePct: Math.round(p.critChance ?? 0),
    critDamagePct: Math.round(p.critDamage ?? 0),
    damageBonusPct: Math.round(p.damageBonus ?? 0),
    attackSpeedBonusPct: Math.round(p.attackSpeedBonus ?? 0),
    lifesteal: Math.round(p.lifesteal ?? 0),
    evasionPct: Math.round(p.evasion ?? 0),
    knockbackBonus: Math.round(p.knockbackBonus ?? 0),
    chestsOpened: state.chestsOpenedThisRun,
    weapons,
    perks,
    items,
    enemiesAlive: alive.length,
    enemyTypes,
  };
}
